import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Divider,
  IconButton,
  Tooltip,
  Collapse,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  Assignment as BitacorasIcon,
  Receipt as FacturaIcon,
  Category as ConceptosIcon,
  CheckCircle as EstatusIcon,
  TypeSpecimen as TiposIcon,
  FactCheck as TimbradasIcon,
  ExpandLess,
  ExpandMore,
  Business as BusinessIcon,
} from '@mui/icons-material';

const Sidebar = ({ open, onClose, collapsed, onToggleCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const menuItems = [
    {
      title: 'Inicio',
      path: '/home',
      icon: <HomeIcon />,
    },
    {
      title: 'Bitácoras',
      path: '/bitacoras',
      icon: <BitacorasIcon />,
    },
    {
      title: 'Facturas Timbradas',
      path: '/facturas-timbradas',
      icon: <TimbradasIcon />,
    },
    {
      title: 'Ingreso de Factura',
      path: '/ingreso-factura',
      icon: <FacturaIcon />,
    },
    {
      title: 'Adenda Bitácora',
      path: '/adenda-bitacora',
      icon: <BitacorasIcon />,
    },
    {
      title: 'Conceptos de Gasto',
      path: '/conceptos-gasto',
      icon: <ConceptosIcon />,
    },
    {
      title: 'Estatus de Factura',
      path: '/estatus-factura',
      icon: <EstatusIcon />,
    },
    {
      title: 'Tipos de Factura',
      path: '/tipos-factura',
      icon: <TiposIcon />,
    },
  ];

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          background: 'linear-gradient(135deg, #00AEEF 0%, #0078d4 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          minHeight: 64,
        }}
      >
        <BusinessIcon sx={{ mr: 1, fontSize: 28 }} />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" noWrap>
            Cryo Facturación
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>
            Sistema de Gestión
          </Typography>
        </Box>
        {!isMobile && (
          <IconButton
            onClick={onToggleCollapse}
            sx={{ color: 'white' }}
            size="small"
          >
            {collapsed ? <ExpandMore /> : <ExpandLess />}
          </IconButton>
        )}
      </Box>

      <Divider />

      {/* Navigation */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <List sx={{ px: 1, py: 2 }}>
          {menuItems.map((item) => {
            const active = isActive(item.path);
            return (
              <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    borderRadius: 1,
                    backgroundColor: active ? 'primary.main' : 'transparent',
                    color: active ? 'white' : 'text.primary',
                    '&:hover': {
                      backgroundColor: active ? 'primary.dark' : 'action.hover',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: active ? 'white' : 'text.secondary',
                      minWidth: 40,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!collapsed && (
                    <ListItemText
                      primary={item.title}
                      primaryTypographyProps={{
                        fontSize: '0.9rem',
                        fontWeight: active ? 600 : 400,
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* Footer */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary" align="center" display="block">
          © {new Date().getFullYear()} Logística Lyta
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
            border: 'none',
            boxShadow: '0 0 20px rgba(0,0,0,0.1)',
            zIndex: theme.zIndex.drawer,
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: collapsed ? 64 : 280,
            border: 'none',
            boxShadow: '0 0 20px rgba(0,0,0,0.1)',
            position: 'fixed',
            top: 0,
            left: 0,
            height: '100vh',
            zIndex: theme.zIndex.drawer,
            transition: 'width 0.3s ease',
            overflowX: 'hidden',
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Sidebar;
